import ace, { Ace } from 'ace-builds';
import React, { useEffect } from 'react';

import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-java';
import 'ace-builds/src-noconflict/theme-chrome';
import { Flexer } from '../dashboard/elements/LayoutElements';

export type CodeEditorProps = {
    mode: Ace.SyntaxMode,
    completer?: Ace.Completer,
    initialValue: string,
    width?: number | string,
    height?: number | string,
    onChange(value: string): void,
};

export const CodeEditor = ({ mode, completer, width, height, initialValue, onChange }: CodeEditorProps) => {
    width = width ?? '100%';
    height = height ?? '100%';

    const [elmt, setElmt] = React.useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!elmt) return;

        const editor = ace.edit(elmt);
        editor.setOptions({
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: false,

            // editor options
            selectionStyle: 'line', // "line"|"text"
            highlightActiveLine: false, // boolean
            highlightSelectedWord: true, // boolean
            readOnly: false, // boolean: true if read only
            cursorStyle: 'ace', // "ace"|"slim"|"smooth"|"wide"
            mergeUndoDeltas: true, // false|true|"always"
            behavioursEnabled: true, // boolean: true if enable custom behaviours
            wrapBehavioursEnabled: true, // boolean
            autoScrollEditorIntoView: undefined, // boolean: this is needed if editor is inside scrollable page
            keyboardHandler: null, // function: handle custom keyboard events

            // renderer options
            animatedScroll: false, // boolean: true if scroll should be animated
            displayIndentGuides: false, // boolean: true if the indent should be shown. See 'showInvisibles'
            showInvisibles: false, // boolean -> displayIndentGuides: true if show the invisible tabs/spaces in indents
            showPrintMargin: false, // boolean: true if show the vertical print margin
            printMarginColumn: 80, // number: number of columns for vertical print margin
            printMargin: undefined, // boolean | number: showPrintMargin | printMarginColumn
            showGutter: false, // boolean: true if show line gutter
            fadeFoldWidgets: false, // boolean: true if the fold lines should be faded
            showFoldWidgets: false, // boolean: true if the fold lines should be shown ?
            showLineNumbers: false,
            highlightGutterLine: false, // boolean: true if the gutter line should be highlighted
            hScrollBarAlwaysVisible: false, // boolean: true if the horizontal scroll bar should be shown regardless
            vScrollBarAlwaysVisible: false, // boolean: true if the vertical scroll bar should be shown regardless
            fontSize: 16, // number | string: set the font size to this many pixels
            fontFamily: 'Fira Code', // string: set the font-family css value
            maxLines: undefined, // number: set the maximum lines possible. This will make the editor height changes
            minLines: undefined, // number: set the minimum lines possible. This will make the editor height changes
            maxPixelHeight: 0, // number -> maxLines: set the maximum height in pixel, when 'maxLines' is defined.
            scrollPastEnd: 0, // number -> !maxLines: if positive, user can scroll pass the last line and go n * editorHeight more distance
            fixedWidthGutter: false, // boolean: true if the gutter should be fixed width
            theme: 'ace/theme/chrome', // theme string from ace/theme or custom?

            // mouseHandler options
            scrollSpeed: 2, // number: the scroll speed index
            dragDelay: 0, // number: the drag delay before drag starts. it's 150ms for mac by default
            dragEnabled: true, // boolean: enable dragging
            focusTimeout: 0, // number: the focus delay before focus starts.
            tooltipFollowsMouse: true, // boolean: true if the gutter tooltip should follow mouse

            // session options
            firstLineNumber: 1, // number: the line number in first line
            overwrite: false, // boolean
            newLineMode: 'auto', // "auto" | "unix" | "windows"
            useWorker: true, // boolean: true if use web worker for loading scripts
            useSoftTabs: true, // boolean: true if we want to use spaces than tabs
            tabSize: 4, // number
            wrap: true, // boolean | string | number: true/'free' means wrap instead of horizontal scroll, false/'off' means horizontal scroll instead of wrap, and number means number of column before wrap. -1 means wrap at print margin
            indentedSoftWrap: false, // boolean
            foldStyle: 'markbegin', // enum: 'manual'/'markbegin'/'markbeginend'.

        });

        editor.getSession().setMode(mode);

        if (completer) {
            editor.completers = [completer];
        }
        else {
            editor.completers = [];
        }

        editor.commands.on('afterExec', function (e) {
            if (e.command.name === 'insertstring') {
                editor.commands.exec('startAutocomplete', editor, []);
            }
        });

        editor.setValue(initialValue);
        editor.on('change', () => {
            onChange(editor.getValue());
        });

        return () => editor.destroy();

    }, [elmt]);

    return <Flexer ref={setElmt} {...{ width, height }} margin=".125 0" />;
};
